import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Wrench, History, MessageSquarePlus, PanelLeftClose, PanelLeftOpen, Settings as SettingsIcon, Store } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { DemoBadge } from "@/components/chat/DemoBadge";
import { Button } from "@/components/ui/button";
import { UserMenu } from "@/components/auth/UserMenu";
import { TesseraAccesso } from "@/components/account/TesseraAccesso";
import { testoStoricoNonDisponibile, useConversations } from "@/hooks/useConversations";
import { useSidebar } from "@/hooks/useSidebar";
import { useAuth } from "@/hooks/useAuth";
import { useBanco } from "@/hooks/useBanco";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export function DesktopSidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { items, storicoNonDisponibile } = useConversations();
  const { open, toggle } = useSidebar();
  const { user, profile } = useAuth();
  const banco = useBanco();

  const recents = items.slice(0, 20);

  if (!open) {
    // Strip mini: solo pulsante per riaprire
    return (
      <aside
        aria-label="Navigazione Datacom AI"
        className="hidden lg:flex h-full w-12 shrink-0 flex-col items-center border-r border-border/60 bg-surface/40 py-3"
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggle}
              aria-label="Apri menu"
              className="h-9 w-9"
            >
              <PanelLeftOpen className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Apri menu</TooltipContent>
        </Tooltip>
      </aside>
    );
  }

  return (
    <aside
      aria-label="Navigazione Datacom AI"
      className="hidden lg:flex h-full w-72 shrink-0 flex-col border-r border-border/60 bg-surface/40"
    >
      <div className="flex items-center justify-between gap-2 px-3 pt-3 pb-3">
        <div className="min-w-0 flex-1 pl-1">
          <Logo size="sm" showWordmark />
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggle}
              aria-label="Riduci menu"
              className="h-8 w-8 shrink-0"
            >
              <PanelLeftClose className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Riduci menu</TooltipContent>
        </Tooltip>
      </div>

      <div className="px-3 pb-2">
        <Button
          onClick={() => navigate("/chat?new=1")}
          className="w-full justify-start gap-2"
          variant="secondary"
        >
          <MessageSquarePlus className="h-4 w-4" />
          Nuova chat
        </Button>
      </div>

      {recents.length > 0 && (
        <div className="px-4 pt-3 pb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Conversazioni recenti
        </div>
      )}
      <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {recents.length === 0 ? (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">
            {storicoNonDisponibile ? (
              <p>{testoStoricoNonDisponibile(storicoNonDisponibile)}</p>
            ) : (
              <>
                <p>Nessuna conversazione ancora.</p>
                <p className="mt-1 text-xs">
                  Scegli una domanda rapida o scrivi la tua per iniziare.
                </p>
              </>
            )}
          </div>
        ) : (
          <ul className="space-y-0.5">
            {recents.map((c) => {
              const active = pathname === `/chat/${c.id}`;
              return (
                <li key={c.id}>
                  <Link
                    to={`/chat/${c.id}`}
                    className={cn(
                      "block truncate rounded-md px-2 py-1.5 text-sm transition-colors",
                      active
                        ? "bg-primary/15 text-foreground"
                        : "text-muted-foreground hover:bg-surface hover:text-foreground",
                    )}
                  >
                    {c.title}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </nav>

      <div className="border-t border-border/60 px-2 py-2">
        <NavLink
          to="/history"
          className={({ isActive }) =>
            cn(
              "flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors",
              isActive
                ? "bg-primary/15 text-foreground"
                : "text-muted-foreground hover:bg-surface hover:text-foreground",
            )
          }
        >
          <History className="h-4 w-4" />
          Storico
        </NavLink>
        <NavLink
          to="/strumenti"
          className={({ isActive }) =>
            cn(
              "flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors",
              isActive
                ? "bg-primary/15 text-foreground"
                : "text-muted-foreground hover:bg-surface hover:text-foreground",
            )
          }
        >
          <Wrench className="h-4 w-4" />
          Strumenti
        </NavLink>
        {/* La chat col banco compare SOLO a chi Datacom l'ha concessa
            (come i marchi) o a chi ha una richiesta di tecnico aperta: per
            gli altri la voce non esiste, non e' una voce spenta (Davide,
            01/09). Chi RISPONDE (account interni) vede la postazione. */}
        {banco.operatore ? (
          <NavLink
            to="/banco-operatore"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors",
                isActive
                  ? "bg-primary/15 text-foreground"
                  : "text-muted-foreground hover:bg-surface hover:text-foreground",
              )
            }
          >
            <Store className="h-4 w-4" />
            Postazione banco
          </NavLink>
        ) : banco.cliente ? (
          <NavLink
            to="/banco"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors",
                isActive
                  ? "bg-primary/15 text-foreground"
                  : "text-muted-foreground hover:bg-surface hover:text-foreground",
              )
            }
          >
            <Store className="h-4 w-4" />
            Banco Datacom
          </NavLink>
        ) : null}
        {/* Niente «Glossario» nel menu (16/10): 76 voci scritte a mano, zero
            NOTIFIER e non filtrate per account → il funzionario Honeywell
            vedeva le sigle degli altri e nessuna delle sue. La pagina /aliases
            resta raggiungibile; i suggerimenti sulle sigle in chat restano. */}
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            cn(
              "flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors",
              isActive
                ? "bg-primary/15 text-foreground"
                : "text-muted-foreground hover:bg-surface hover:text-foreground",
            )
          }
        >
          <SettingsIcon className="h-4 w-4" />
          Impostazioni
        </NavLink>
      </div>

      <div className="border-t border-border/60 px-2 py-1.5">
        <TesseraAccesso compatta />
      </div>

      {(profile || user) && (
        <div className="flex items-center gap-2 border-t border-border/60 px-3 py-2.5">
          <UserMenu size="sm" />
          <span className="truncate text-sm font-medium text-foreground">
            {profile
              ? `${profile.first_name} ${profile.last_name ? profile.last_name[0] + "." : ""}`.trim()
              : (user?.email ?? "Account")}
          </span>
        </div>
      )}

      <div className="border-t border-border/60 px-3 py-3">
        <DemoBadge />
      </div>
    </aside>
  );
}